import { useEffect, useRef, useState } from "react";
import { FaChevronDown, FaBars, FaTimes } from "react-icons/fa";
import { Link, useLocation, useNavigate } from "react-router-dom";
import q from "../assets/q.png";
import "./Header.css";

export const Header = ({ openForm, language, toggleLanguage }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const headerRef = useRef(null);
  const dropdownRef = useRef(null);
  const dropdownCloseTimer = useRef(null);
  const isArabic = language === "ar";

  const openDropdown = () => {
    if (dropdownCloseTimer.current) {
      window.clearTimeout(dropdownCloseTimer.current);
      dropdownCloseTimer.current = null;
    }
    setIsDropdownOpen(true);
  };

  const scheduleCloseDropdown = () => {
    if (dropdownCloseTimer.current) {
      window.clearTimeout(dropdownCloseTimer.current);
    }
    dropdownCloseTimer.current = window.setTimeout(() => {
      setIsDropdownOpen(false);
      dropdownCloseTimer.current = null;
    }, 180);
  };
  const localeFont = isArabic ? "font-arabic2" : "font-english2";
  const isHome = location.pathname === "/";

  const syncHeaderHeight = () => {
    const height = headerRef.current?.offsetHeight ?? 88;
    document.documentElement.style.setProperty("--app-header-h", `${height}px`);
  };

  const getHeaderOffset = () => headerRef.current?.offsetHeight ?? 88;

  const handleSmoothScroll = (e, targetId) => {
    e.preventDefault();

    if (!isHome) {
      navigate("/", { state: { scrollTo: targetId } });
      setIsMobileMenuOpen(false);
      return;
    }

    const targetElement = document.querySelector(targetId);
    if (targetElement) {
      const elementPosition = targetElement.offsetTop - getHeaderOffset();
      window.scrollTo({ top: elementPosition, behavior: "smooth" });
      setIsMobileMenuOpen(false);
    }
  };

  useEffect(() => {
    if (location.state?.scrollTo && isHome) {
      const targetElement = document.querySelector(location.state.scrollTo);
      if (targetElement) {
        const elementPosition = targetElement.offsetTop - getHeaderOffset();
        setTimeout(() => {
          window.scrollTo({ top: elementPosition, behavior: "smooth" });
        }, 100);
        navigate("/", { replace: true, state: {} });
      }
    }
  }, [location, navigate, isHome]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      if (dropdownCloseTimer.current) {
        window.clearTimeout(dropdownCloseTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    syncHeaderHeight();
    const onResize = () => syncHeaderHeight();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [isMobileMenuOpen, language]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsDropdownOpen(false);
  }, [location.pathname]);

  const joinLabel = language === "en" ? "Join Qargoquote" : "انضم لـ Qargoquote";
  const langGlyph = language === "en" ? "ع" : "E";

  return (
    <header
      ref={headerRef}
      className={`site-header ${isScrolled ? "site-header--scrolled" : ""} ${
        isArabic ? "site-header--rtl" : ""
      }`}
    >
      <div className="site-header__shell">
        <Link to="/" className="site-header__logo" onClick={() => setIsMobileMenuOpen(false)}>
          <img src={q} alt="QargoQuote" />
        </Link>

        <nav
          dir={isArabic ? "rtl" : "ltr"}
          className={`site-header__nav ${localeFont}`}
          aria-label={isArabic ? "القائمة الرئيسية" : "Primary"}
        >
          <div
            className="site-header__dropdown-wrap"
            onMouseEnter={openDropdown}
            onMouseLeave={scheduleCloseDropdown}
            ref={dropdownRef}
          >
            <button
              type="button"
              className="site-header__link"
              style={{ display: "inline-flex", alignItems: "center" }}
              onClick={() => setIsDropdownOpen((v) => !v)}
              aria-expanded={isDropdownOpen}
            >
              {language === "en" ? "Solutions" : "الحلول"}
              <FaChevronDown className={`site-header__chevron ${isDropdownOpen ? "is-open" : ""}`} />
            </button>

            <div className={`site-header__dropdown ${isDropdownOpen ? "is-open" : ""}`}>
              <div className="site-header__dropdown-panel">
                <ul>
                  <li>
                    <button
                      type="button"
                      onClick={() => {
                        navigate("/service-provider");
                        setIsDropdownOpen(false);
                      }}
                    >
                      {language === "en" ? "Service providers" : "مقدمو الخدمات"}
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      onClick={() => {
                        navigate("/importer");
                        setIsDropdownOpen(false);
                      }}
                    >
                      {language === "en" ? "Importers and traders" : "المستوردون والتجار"}
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <button type="button" onClick={(e) => handleSmoothScroll(e, "#FAQ")} className="site-header__link">
            {language === "en" ? "FAQ" : "الأسئلة الشائعة"}
          </button>

          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              openForm();
            }}
            className="site-header__link"
          >
            {language === "en" ? "Contact" : "تواصل"}
          </button>

          <Link to="/career" className="site-header__link">
            {language === "en" ? "Career" : "توظيف"}
          </Link>
        </nav>

        <div className={`site-header__actions ${localeFont}`}>
          <button
            type="button"
            onClick={toggleLanguage}
            className="site-header__lang"
            aria-label={language === "en" ? "Switch to Arabic" : "Switch to English"}
          >
            {langGlyph}
          </button>

          <a
            href="https://qatar.qargoquote.com/login"
            target="_blank"
            rel="noopener noreferrer"
            className="site-header__login"
          >
            {language === "en" ? "Login" : "دخول"}
          </a>

          <a
            href="https://qatar.qargoquote.com/signUp"
            target="_blank"
            rel="noopener noreferrer"
            className="site-header__cta"
          >
            <span>{joinLabel}</span>
            <span className="site-header__cta-arrow" aria-hidden>
              {isArabic ? "←" : "→"}
            </span>
          </a>

          <button
            type="button"
            onClick={() => setIsMobileMenuOpen((prev) => !prev)}
            className="site-header__menu-btn"
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? <FaTimes className="h-4 w-4" /> : <FaBars className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <div className={`site-header__panel ${isMobileMenuOpen ? "is-open" : ""}`}>
        <div className={`site-header__panel-card ${localeFont}`}>
          <button
            type="button"
            onClick={() => {
              navigate("/service-provider");
              setIsMobileMenuOpen(false);
            }}
            className="site-header__mobile-link"
          >
            {language === "en" ? "Service providers" : "مقدمو الخدمات"}
          </button>

          <button
            type="button"
            onClick={() => {
              navigate("/importer");
              setIsMobileMenuOpen(false);
            }}
            className="site-header__mobile-link"
          >
            {language === "en" ? "Importers and traders" : "المستوردون والتجار"}
          </button>

          <button type="button" onClick={(e) => handleSmoothScroll(e, "#FAQ")} className="site-header__mobile-link">
            {language === "en" ? "FAQ" : "الأسئلة الشائعة"}
          </button>

          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              openForm();
              setIsMobileMenuOpen(false);
            }}
            className="site-header__mobile-link"
          >
            {language === "en" ? "Contact" : "تواصل"}
          </button>

          <Link to="/career" className="site-header__mobile-link" onClick={() => setIsMobileMenuOpen(false)}>
            {language === "en" ? "Career" : "توظيف"}
          </Link>

          <a
            href="https://qatar.qargoquote.com/login"
            target="_blank"
            rel="noopener noreferrer"
            className="site-header__mobile-link"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            {language === "en" ? "Login" : "دخول"}
          </a>

          <div className="site-header__panel-cta">
            <a
              href="https://qatar.qargoquote.com/signUp"
              target="_blank"
              rel="noopener noreferrer"
              className="site-header__cta"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <span>{joinLabel}</span>
              <span className="site-header__cta-arrow" aria-hidden>
                {isArabic ? "←" : "→"}
              </span>
            </a>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
